<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <!-- Page Header -->
    <div class="page-header-with-filters">
      <d-row
        no-gutters
        class="page-header py-4">
        <d-col
          col
          sm="6"
          class="text-center text-sm-left mb-4 mb-sm-0">
          <span class="text-uppercase page-subtitle">{{ $t('service.services') }}</span>
          <h3 class="page-title">
            <template v-if="isIndentList">{{ $t('service.servicesIndentList') }}</template>
            <template v-else>{{ $t('service.servicesList') }}</template>
          </h3>
        </d-col>
        <d-col
          col
          sm="6"
          class="text-right text-sm-right d-flex justify-content-end align-items-end">
          <d-button
            theme="success"
            @click.prevent="$router.push(addUrl)">
            <i class="material-icons">add</i>
            {{ $t('service.addService') }}
          </d-button>
        </d-col>
      </d-row>

      <list-filters
        :config="filtersConfig"
        :isListLoaded="allDataLoaded"
        :view-name="'services'"
        @init="handleFiltersInit"
        @search="handleFiltersSearch" />
    </div>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th
              scope="col"
              class="border-0">{{ $t('service.serviceName') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('service.serviceCode') }}</th>
            <th
              scope="col"
              class="border-0"
              style="min-width: 200px;">{{ $t('service.setupPrice') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('service.servicesGroup') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="allDataLoaded">
          <tr
            v-for="(service, index) of services"
            :key="'service-' + index">
            <td class="align-middle">
              {{ service.name }}<br>
              <small :class="service.price_multiple_from === 0 ? 'text-secondary' : 'text-warning'">
                <template v-if="service.price_multiple_from === 0">{{ $t('service.priceAlwaysApiece') }}</template>
                <template v-else>{{ $t('ui.toCap') }} {{ service.price_multiple_from }} {{ $t('ui.piecesAbbr') }} {{ $t('service.priceForAll') }}</template>
              </small>
              <template v-if="service.available_in_embriodery">
                <br>
                <d-badge theme="success">
                  {{ $t('service.availableInHafciarnia') }}
                </d-badge>
              </template>
            </td>
            <td class="align-middle">
              {{ service.code }}
            </td>
            <td
              class="align-middle"
              v-pure-html="getSetupPrices(service.service_prices)"></td>
            <td class="align-middle">
              <template v-if="service.group">{{ service.group.name }}</template>
              <template v-else>-</template>
            </td>
            <td
              class="align-middle"
              style="width: 150px;">
              <d-button
                theme="primary"
                @click.native="$router.push(isIndentList ? '/zdobienia-indent/edycja/' + service.id : '/zdobienia/edycja/' + service.id)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                theme="danger"
                :disabled="removeInProgress"
                @click.native="removeService(service.id)"
                :title="$t('ui.delete')">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="services.length === 0">
            <td
              colspan="5"
              class="text-center">
              {{ $t('service.noServicesToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!allDataLoaded"
        class="pb-3 pt-3 text-center">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a
          href="javascript:window.location.reload();"
        class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import CommonGetters from './../utils/CommonGetters.js';
import debounce from 'lodash.debounce';
import FormUtils from './../utils/FormUtils.js';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';
import cloneDeep from 'lodash.clonedeep';

export default {
  name: 'services',
  mixins: [
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  computed: {
    allDataLoaded () {
      return this.loadedCurrencies && this.loaded;
    },
    addUrl () {
      if (this.isIndentList) {
        return '/zdobienia-indent/nowe';
      }

      return '/zdobienia/nowe';
    },
    isIndentList () {
      return this.$route.path.indexOf('zdobienia-indent') > -1;
    },
    filtersConfig () {
      return [
        {
          key: 'search',
          apiKey: 'search',
          label: this.$t('ui.search'),
          placeholder: this.$t('service.serviceSearchPlaceholder'),
          type: 'text',
          isBasic: true,
          defaultValue: ''
        }
      ];
    }
  },
  data () {
    return {
      currencies: [],
      services: [],
      loaded: false,
      loadedCurrencies: false,
      loadError: false,
      removeInProgress: false,
      activeFilters: {},
      apiQueryPayload: {}
    };
  },
  mounted () {
    this.loadAdditionalData();
  },
  methods: {
    handleFiltersInit (filters, apiPayload) {
      this.activeFilters = filters;
      this.apiQueryPayload = apiPayload;

      this.loadFilteredData();
    },
    handleFiltersSearch (activeFilters, apiPayload) {
      this.activeFilters = activeFilters;
      this.apiQueryPayload = apiPayload;
      this.$bus.$emit('pagination-reset');
      this.debouncedLoadFilteredData();
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/currencies/items',
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError'
      });
    },
    debouncedLoadFilteredData: debounce(function () {
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = cloneDeep(this.apiQueryPayload) || {};

      where.is_indent = this.isIndentList ? 1 : 0;

      ListUtils.loadPaginatedItemsData(this, {
        method: 'get',
        endpoint: '/services/items?page=' + this.currentPage + '&lang=' + this.$store.getters.getUserLang,
        listField: 'services',
        params: {
          with: ['servicePrices'],
          where
        }
      });
    },
    removeService (serviceID) {
      this.removeInProgress = true;
      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/services/delete/',
        id: serviceID,
        successTitle: this.$t('service.deleteSuccessTitle'),
        successText: this.$t('service.deleteSuccessText'),
        confirmTitle: this.$t('service.deleteConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('service.deleteErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    getSetupPrices (servicePrices) {
      let output = [];

      let setupPrices = servicePrices.filter(price => price.is_setup);
      setupPrices.sort((itemA, itemB) => itemA.value_from - itemB.value_from);

      for (let i = 0; i < setupPrices.length; i++) {
        output.push(Number(setupPrices[i].price).toFixed(2) + ' ' + this.getCurrency(setupPrices[i].currency_id) + ' (' + this.$t('ui.to') + ' ' + parseInt(setupPrices[i].value_from, 10) + ' ' + this.$t('ui.piecesAbbr') + ')');
      }

      return '<small>' + output.join('<br>') + '</small>';
    },
    getCurrency (currencyID) {
      return CommonGetters.getCurrencyById(currencyID, this.currencies);
    }
  }
};
</script>
